import { Compatibility } from '@music-vine/setlist';

import type { CompatibilityType } from '@music-vine/setlist';

import type { Checkbox as CheckboxType } from 'radix-ui';

type CheckedState = CheckboxType.CheckedState;

export interface FilterState {
  excluded: string[];
  included: string[];
}

export type FilterAction = 'included' | 'excluded';

export type FilterBehavior = 'replace' | 'additive';

export type FilterLogic = 'and' | 'or';

export type FilterFieldName = string;

export type FilterFieldValue = string;

export interface FilterConfig {
  behavior: FilterBehavior;
  logic: FilterLogic;
}

export type FilterConfigs = Record<string, FilterConfig>;

export interface FacetParent {
  drag_and_drop?: boolean;
  id: number;
  name: string;
}

export interface FacetCount {
  count: number;
  highlighted?: string;
  parent?: FacetParent | FacetParent[];
  value: string;
}

export interface SecondaryToggles {
  noneSelected: boolean;
  show: boolean;
  values: SecondaryToggle[];
}

export interface SecondaryToggle {
  hide?: boolean;
  id: string;
  label: string;
  value: CheckedState;
}

export interface FilterCategory {
  config: FilterConfig;
  fieldName: string;
  id: string;
  isAdvanced?: boolean;
  label: string;
  options?: ToggleOption[]; // Options for exclusive-selection filters like SFX classification
  searchable?: boolean;
  suggestionFieldName?: string; // Optional field name for search suggestions (defaults to fieldName)
}

export interface ToggleOption {
  color?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  id: string;
  label?: string;
  secondaryToggles?: SecondaryToggle[];
  tooltip?: string;
}

export interface AssetTypeConfig {
  // Core filtering
  coreFilter?: string;
  displayName: string;

  // Filter categories
  filterCategories: FilterCategory[];

  // Advanced filter toggle
  hasAdvancedFilters?: boolean;
  // Core identity
  id: string;

  // Parent field mappings for facet data
  parentFieldMappings?: Record<string, string>;

  // Primary toggles (like compatibility for motion graphics)
  primaryToggles?: {
    category: string;
    fieldName: string;
    options: ToggleOption[];
  };

  // Search configuration
  searchFields?: string[];

  // Typesense configuration
  typesenseCollection: string;
}

// Asset type IDs
export const ASSET_TYPES = {
  MOTION_GRAPHICS: 'motion-graphics',
  MUSIC: 'music',
  SFX: 'sfx',
  STOCK_VIDEO: 'stock-video',
} as const;

export type AssetTypeId = (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES];

export { Compatibility };
export type { CompatibilityType };

// SFX Classification constants
export const SfxClassification = {
  VoicesSpeech: 'Voices & speech',
  EnvironmentalAmbience: 'Environmental & ambience',
  SoundDesign: 'Sound design',
  MusicClipsStings: 'Music clips & stings',
  RecordedSounds: 'Recorded sounds',
} as const;

export type ActivePrimaryToggle =
  | `${typeof Compatibility.AfterEffects}`
  | `${typeof Compatibility.PremierePro}`
  | `${typeof Compatibility.DaVinciResolve}`
  | `${typeof Compatibility.FinalCut}`;
